/**
 * Module providing utilities to read, set and clear browser cookies
 *
 * @author Atomic Smash <developers@atomicsmash.co.uk>
 * @version 1.0.0
 */

/** Valid cookie values */
type Value = string | number | { [key: string]: Value };

/** Start of UNIX epoch */
const THE_PAST = "Thu, 01-Jan-1970 00:00:01 GMT";

/**
 * Basic function to construct simple cookie strings
 *
 * @internal
 * @param name    Cookie name
 * @param value   Cookie value
 * @param expires Cookie expiry date
 */
const cookieString = (name: string, value: string, expires: string): string => {
    return `${name}=${value};expires=${expires}`;
};

/**
 * Deletes a cookie by setting it's value to empty and expiry in the past
 *
 * @param name Cookie name
 */
export const deleteCookie = (name: string): void => {
    const cookie = cookieString(name, "", THE_PAST);

    document.cookie = cookie;
};

/**
 * Sets a cookie with the given name and value(s)
 *
 * @param name   Cookie name
 * @param value  Cookie value
 * @param expires Cookie expiry date
 */
export const setCookie = (name: string, value: Value, expires: string): void => {
    const cookie = cookieString(name, JSON.stringify(value), expires);

    document.cookie = cookie;
};

/**
 * Returns the value of the named cookie
 *
 * @param name Cookie name
 * @return     Cookie value (or null)
 */
export const getCookie = <T extends Value>(name: string): T|null => {
    const allCookies = document.cookie.split(";");

    /*
     * First trim the cookies to remove any white-space before/after the string,
     * then see if we can find one that matches the requested `name` parameter.
     */
    const cookie = allCookies.map((cookieString) => {
        return cookieString.trim();
    }).find((cookieString) => {
        return cookieString.startsWith(`${name}=`);
    });

    // No cookie value for `name` found
    if (!cookie) {
        return null;
    }

    const [, value] = cookie.split("=", 2);

    // Looks JSON like, attempt to parse it...
    if (value.startsWith("{") || value.startsWith("[")) {
        return JSON.parse(value);
    }

    // ...otherwise it's probably just a string value
    return value as (T & string);
};
